import { getMannequinVariantById } from "@poseref/shared";
import { RefreshCw } from "lucide-react";
import { useCallback, useState } from "react";
import { useIsTrial } from "../../app/authStore.ts";
import { useStore } from "../../app/store.ts";
import {
  AVAILABLE_BODY_TYPES,
  DEFAULT_MODEL_ID,
} from "../../mannequin/registry.ts";
import { Button } from "../Button.tsx";
import { CharacterProfileSection } from "../CharacterProfilePanel.tsx";
import { useDeferredUndo } from "../hooks/useDeferredUndo.ts";
import { SidebarCard } from "../SidebarCard.tsx";
import { Divider, SectionLabel, SidebarButton, SliderRow } from "../shared.tsx";
import {
  COLOR_ACTIVE_BG,
  COLOR_BORDER,
  COLOR_BORDER_ACCENT,
  COLOR_GOLD,
  COLOR_SURFACE,
  COLOR_TEXT,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_MUTED,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_XS,
} from "../tokens.ts";

// Proportion controls use volume region IDs as store keys.
// Grouped by body area for the UI.
const PROPORTION_GROUPS: {
  label: string;
  regions: { id: string; label: string }[];
}[] = [
  {
    label: "Torso",
    regions: [
      { id: "chest", label: "Chest" },
      { id: "buttocks", label: "Buttocks" },
      { id: "waist", label: "Waist" },
    ],
  },
  {
    label: "Arms",
    regions: [
      { id: "biceps", label: "Biceps" },
      { id: "forearms", label: "Forearms" },
    ],
  },
  {
    label: "Legs",
    regions: [
      { id: "thighs", label: "Thighs" },
      { id: "calves", label: "Calves" },
    ],
  },
];

export function MannequinMorphPanel({ objectId }: { objectId: string }) {
  const currentModelId = useStore((s) => {
    const obj = s.sceneObjects.get(objectId);
    return obj?.objectKind === "mannequin"
      ? (obj.mannequinModelId ?? DEFAULT_MODEL_ID)
      : DEFAULT_MODEL_ID;
  });
  const setModelId = useStore((s) => s.setMannequinModelId);
  const isTrial = useIsTrial();

  const [showProportions, setShowProportions] = useState(false);

  return (
    <SidebarCard style={{ overflow: "hidden" }}>
      <SectionLabel>Body Type</SectionLabel>
      {AVAILABLE_BODY_TYPES.length > 1 ? (
        <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
          {AVAILABLE_BODY_TYPES.map((bt) => {
            const isLocked = isTrial && bt !== "male-neutral";
            return (
              <Button
                key={bt}
                onClick={isLocked ? undefined : () => setModelId(objectId, bt)}
                disabled={isLocked}
                title={
                  isLocked ? "Subscribe to access more body types" : undefined
                }
                variant="secondary"
                size="xs"
                style={{
                  flex: 1,
                  padding: "4px 8px",
                  border: `1px solid ${isLocked ? COLOR_BORDER : COLOR_BORDER_ACCENT}`,
                  borderRadius: RADIUS_XS,
                  background:
                    bt === currentModelId ? COLOR_ACTIVE_BG : COLOR_SURFACE,
                  color: isLocked
                    ? COLOR_TEXT_FAINT
                    : bt === currentModelId
                      ? COLOR_TEXT
                      : COLOR_TEXT_MUTED,
                  fontWeight: bt === currentModelId ? 600 : 400,
                  opacity: isLocked ? 0.5 : 1,
                  position: "relative",
                }}
              >
                {getMannequinVariantById(bt)?.shortLabel ?? bt}
                {isLocked && (
                  <span
                    style={{
                      display: "block",
                      fontSize: FONT_SIZE_TINY,
                      color: COLOR_GOLD,
                      fontWeight: 600,
                      lineHeight: 1,
                      marginTop: 1,
                      opacity: 0.8,
                    }}
                  >
                    Pro
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_FAINT,
            marginBottom: 8,
          }}
        >
          {AVAILABLE_BODY_TYPES.length === 1
            ? (getMannequinVariantById(AVAILABLE_BODY_TYPES[0])?.shortLabel ??
              AVAILABLE_BODY_TYPES[0])
            : "No variants built yet"}
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <SidebarButton
          icon={null}
          label={showProportions ? "Hide Proportions" : "Edit Proportions"}
          onClick={() => setShowProportions(!showProportions)}
          active={showProportions}
        />
      </div>

      <ConstraintWeightSlider objectId={objectId} />

      {showProportions && (
        <div style={{ marginTop: 8 }}>
          {PROPORTION_GROUPS.map((group) => (
            <div key={group.label} style={{ marginBottom: 6 }}>
              <SectionLabel>{group.label}</SectionLabel>
              {group.regions.map((region) => (
                <BoneVolumeRow
                  key={region.id}
                  objectId={objectId}
                  regionId={region.id}
                  label={region.label}
                />
              ))}
            </div>
          ))}
          <div style={{ marginTop: 6 }}>
            <SidebarButton
              icon={<RefreshCw size={14} />}
              label="Reset Proportions"
              active={false}
              onClick={() => {
                const s = useStore.getState();
                s.pushUndo();
                const volumes = new Map(s.boneVolumes);
                volumes.delete(objectId);
                useStore.setState({ boneVolumes: volumes });
              }}
            />
          </div>
        </div>
      )}

      {/* Character Profiles — save/load body configurations */}
      <Divider />
      <CharacterProfileSection objectId={objectId} />
    </SidebarCard>
  );
}

function BoneVolumeRow({
  objectId,
  regionId,
  label,
}: {
  objectId: string;
  regionId: string;
  label: string;
}) {
  const scale = useStore(
    (s) => s.boneVolumes.get(objectId)?.get(regionId) ?? 1.0,
  );
  const setVolume = useStore((s) => s.setBoneVolume);
  const undo = useDeferredUndo();

  const handleChange = useCallback(
    (v: number) => {
      setVolume(objectId, regionId, v / 100);
    },
    [objectId, regionId, setVolume],
  );

  return (
    <SliderRow
      density="compact"
      label={label}
      value={Math.round(scale * 100)}
      min={50}
      max={200}
      displayValue={`${Math.round(scale * 100)}%`}
      defaultValue={100}
      onStart={undo.pushOnce}
      onEnd={undo.reset}
      onChange={handleChange}
      dimChromeOnDrag
    />
  );
}

function ConstraintWeightSlider({ objectId }: { objectId: string }) {
  const weight = useStore((s) => s.constraintWeights.get(objectId) ?? 1);
  const setWeight = useStore((s) => s.setConstraintWeight);
  const undo = useDeferredUndo();

  return (
    <div style={{ marginTop: 4 }}>
      <SliderRow
        density="compact"
        label="Constraints"
        value={Math.round(weight * 100)}
        min={0}
        max={100}
        displayValue={`${Math.round(weight * 100)}%`}
        defaultValue={100}
        onStart={undo.pushOnce}
        onEnd={undo.reset}
        onChange={(v) => setWeight(objectId, v / 100)}
        dimChromeOnDrag
      />
    </div>
  );
}
